import { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Highlight, themes } from 'prism-react-renderer';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

// Code block with syntax highlighting and copy button
interface CodeBlockProps {
  code: string;
  language: string;
}

function CodeBlock({ code, language }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [code]);

  // Normalize language name for prism
  const normalizedLanguage = language?.toLowerCase() || 'text';

  return (
    <div className="relative group my-1.5">
      {/* Language badge and copy button */}
      <div className="absolute top-0 right-0 flex items-center gap-1 p-1">
        {language && (
          <span className="text-[10px] text-muted-foreground bg-muted/80 px-1.5 py-0.5 rounded">
            {language}
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={handleCopy}
        >
          {copied ? (
            <Check className="h-3 w-3 text-green-600" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      </div>

      <Highlight theme={themes.nightOwl} code={code.trim()} language={normalizedLanguage}>
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={cn(
              'p-3 pt-6 rounded-md overflow-x-auto text-xs font-mono leading-relaxed',
              className
            )}
            style={{ ...style, backgroundColor: 'hsl(var(--muted))' }}
          >
            {tokens.length > 5 && (
              <div className="absolute left-0 top-6 bottom-0 w-8 text-right pr-2 text-muted-foreground/50 select-none">
                {tokens.map((_, i) => (
                  <div key={i} className="leading-relaxed text-[10px]">
                    {i + 1}
                  </div>
                ))}
              </div>
            )}
            <code className={tokens.length > 5 ? 'block pl-6' : ''}>
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </code>
          </pre>
        )}
      </Highlight>
    </div>
  );
}

export function MarkdownContent({ content, className }: MarkdownContentProps) {
  return (
    <div className={cn('max-w-none text-[13px] leading-normal', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
        // Headers - compact sizing for chat
        h1: ({ children }) => (
          <h1 className="text-sm font-semibold mt-2 mb-1 first:mt-0">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-[13px] font-semibold mt-2 mb-1 first:mt-0">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-[13px] font-medium mt-1.5 mb-0.5 first:mt-0">{children}</h3>
        ),

        // Paragraphs - compact
        p: ({ children }) => <p className="mb-1.5 last:mb-0 leading-snug">{children}</p>,

        // Lists - tight spacing
        ul: ({ children }) => (
          <ul className="list-disc list-outside ml-4 mb-1.5 space-y-0.5 text-[13px]">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-outside ml-4 mb-1.5 space-y-0.5 text-[13px]">{children}</ol>
        ),
        li: ({ children }) => <li className="leading-snug">{children}</li>,

        // Code - inline and block with syntax highlighting
        code: ({ className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className || '');
          const isCodeBlock = match || (typeof children === 'string' && children.includes('\n'));

          if (isCodeBlock) {
            const language = match ? match[1] : '';
            const code = String(children).replace(/\n$/, '');
            return <CodeBlock code={code} language={language} />;
          }

          // Inline code
          return (
            <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono" {...props}>
              {children}
            </code>
          );
        },

        // Pre wrapper - handled by code component
        pre: ({ children }) => <>{children}</>,

        // Links
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 hover:text-primary/80"
          >
            {children}
          </a>
        ),

        // Blockquotes
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-primary/40 pl-2 italic my-1.5 text-muted-foreground text-[13px]">
            {children}
          </blockquote>
        ),

        // Tables
        table: ({ children }) => (
          <div className="overflow-x-auto my-1.5">
            <table className="min-w-full text-xs border-collapse">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
        th: ({ children }) => (
          <th className="border border-border px-1.5 py-1 font-medium text-left text-xs">{children}</th>
        ),
        td: ({ children }) => (
          <td className="border border-border px-1.5 py-1 text-xs">{children}</td>
        ),

        // Strong/Bold
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,

        // Emphasis/Italic
        em: ({ children }) => <em className="italic">{children}</em>,

        // Horizontal rule
        hr: () => <hr className="my-2 border-border" />
      }}
    >
        {content}
      </ReactMarkdown>
    </div>
  );
}
